import {
  FeedFollowBehavior,
  ImageFile,
  isAdminLayout,
  RestrictDuplicateUrlOfCardsInTimeFrame,
  StatusCopyright,
} from '../shared';
import { graphqlApiWithPersisted } from './graphqlApi';
import { storyQuery } from './helpers';
import { OrganisationTag } from './organisation';
import { User, UserPreview } from './user';

export enum StoryStatus {
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHED = 'UNPUBLISHED',
  SCHEDULED = 'SCHEDULED',
}

export enum StorySection {
  WEB_FEED = 'WEB_FEED',
  MOBILE_FEED = 'MOBILE_FEED',
  MIX_LIST = 'MIX_LIST',
  MIX_LIST_2 = 'MIX_LIST_2',
  MIX_LIST_3 = 'MIX_LIST_3',
  MIX_LIST_4 = 'MIX_LIST_4',
  MIX_LIST_5 = 'MIX_LIST_5',
  PINNED = 'PINNED',
  PINNED_2 = 'PINNED_2',
  PINNED_3 = 'PINNED_3',
  PINNED_4 = 'PINNED_4',
  PINNED_5 = 'PINNED_5',
  PINNED_ORG = 'PINNED_ORG', // * custom enum value
  HORIZONTAL = 'HORIZONTAL',
  UNASSIGNED = 'UNASSIGNED',
}

export enum StoryAccess {
  EDITOR_LIMITED = 'EDITOR_LIMITED',
  INDIVIDUAL = 'INDIVIDUAL',
  READER = 'READER',
}

export enum StoryType {
  STANDARD = 'STANDARD',
  READONLY = 'READONLY',
  SYNC = 'SYNC',
}

export enum StoryPinned {
  CHANNEL = 'CHANNEL',
  ORGANISATION = 'ORGANISATION',
}

export enum StoryHighlightingCommentsMode {
  FIRST = 'FIRST',
  MANUAL = 'MANUAL',
  MOST_LIKEABLE = 'MOST_LIKEABLE',
}

export enum StoryCardTeaserImageStyle {
  BIG_WITHOUT_TEXT = 'BIG_WITHOUT_TEXT',
  SMALL_WITHOUT_TEXT = 'SMALL_WITHOUT_TEXT',
  SMALL_WITH_TEXT = 'SMALL_WITH_TEXT',
  STANDARD = 'STANDARD',
}

export enum StoryDisplayItemUpdatedTime {
  POSTED_TIME = 'POSTED_TIME',
  UPDATED = 'UPDATED',
}

export enum StoryCardOrder {
  MANUAL = 'MANUAL',
  POSTED_TIME = 'POSTED_TIME',
}

export interface StoryHorizontalDisplayView {
  position: number;
}

export enum StoryCardLimit {
  ONE_THOUSAND = 'ONE_THOUSAND',
  TWO_THOUSANDS = 'TWO_THOUSANDS',
  FIVE_THOUSANDS = 'FIVE_THOUSANDS',
  TEN_THOUSANDS = 'TEN_THOUSANDS',
  TWENTY_THOUSANDS = 'TWENTY_THOUSANDS',
  UNLIMITED = 'UNLIMITED',
}

export interface StoryAutoNotification {
  enabled: boolean;
  titleDefault: string;
  messageDefault: string;
  titlePrefix: string;
}

interface StoryCardStyles {
  article: { teaserStyle: StoryCardTeaserImageStyle };
  backgroundColor: string;
}

export interface StorySettings {
  access: StoryAccess[];
  allowAccessToEditorLimited: boolean;
  allowAccessToReader: boolean;
  allowCardReactions: boolean;
  allowComments: boolean;
  allowCardMentions: boolean;
  allowSharing: boolean;
  autoNotification: StoryAutoNotification;
  horizontalDisplayView: StoryHorizontalDisplayView;
  includeInMixTab: boolean;
  includeInMixTab2: boolean;
  includeInMixTab3: boolean;
  includeInMixTab4: boolean;
  includeInMixTab5: boolean;
  includeInNewsFeed: boolean;
  includeInWebappNewsFeed: boolean;
  section: StorySection[];
  shouldSyncItemPositionsOnConnectedStories: boolean;
  shouldUpdatePostedTimeByComments: boolean;
  showAllAuthors: boolean;
  cardStyles: StoryCardStyles;
  cardOrder: StoryCardOrder;
  displayItemUpdatedTime: StoryDisplayItemUpdatedTime;
  feedFollowBehavior: FeedFollowBehavior;
  highlightingComments: StoryHighlightingCommentsMode;
  shouldRestrictDuplicateUrlOfCardsInTimeFrame: RestrictDuplicateUrlOfCardsInTimeFrame;
  cardLimit: StoryCardLimit;
}

interface StoryWriteAccess {
  userTags: OrganisationTag[];
  users: UserPreview[];
}

interface SourceChannel {
  id: number;
  name: string;
  subdomain: string;
}

interface SourceStory {
  id: number;
  title: string;
}

export interface SourceStoryInfo {
  channel: SourceChannel;
  story: SourceStory;
}

export interface Story {
  author: User;
  authorId: number;
  cardsCount: number;
  channelId: number;
  feedFollowedStatus: boolean;
  id: number;
  image: ImageFile;
  imageId: number;
  isReadOnly: boolean;
  localeId: number;
  pinned: StoryPinned;
  pinned2: StoryPinned;
  pinned3: StoryPinned;
  pinned4: StoryPinned;
  pinned5: StoryPinned;
  position: number;
  settings: StorySettings;
  sources: SourceStoryInfo[];
  status: StoryStatus;
  subscriptionStatus: boolean;
  subtitle: string;
  title: string;
  type: StoryType;
  externalTags: OrganisationTag[];
  externalStatusTags: OrganisationTag[];
  internalTags?: OrganisationTag[];
  writeAccess?: StoryWriteAccess;
}

interface StoriesByChannelIdResponse {
  data: {
    storiesByChannelId: {
      items: Story[];
    };
  };
}

export interface StoriesByChannelIdFilter {
  onlyPostIn?: boolean;
  status?: StoryStatus[];
  section?: StorySection[];
  access?: StoryAccess[];
  type?: StoryType[];
  feedFollowBehavior?: FeedFollowBehavior[];
  feedFollowedStatus?: boolean;
  query?: string;
}

export interface StoriesByChannelIdArgs {
  channelId: number;
  filter?: StoriesByChannelIdFilter;
}

interface StoriesByIdResponse {
  data: {
    storyById: {
      payload: Story;
    };
  };
}

interface StoriesByIdArgs {
  storyId: number;
}

interface StoryDeleteResponse {
  data: {
    storyDelete: {
      payload: boolean;
    };
  };
}

interface StoryDeleteArgs {
  storyId: number;
}

export interface StoryUpdateArgs {
  storyId: number;
  fields: Partial<Story>;
}

interface StoryUpdateResponse {
  data: {
    storyUpdate: {
      payload: Story;
    };
  };
}

interface StoryUpdatePositionArgs {
  storyId: number;
  position: number;
}

interface StorySourceInput {
  storyId: number;
  fetchLastCards?: number;
}

interface StoryCreateFieldsSourceInput {
  sources: StorySourceInput[];
  isReadOnly?: boolean;
}

interface StoryUpdatePositionResponse {
  data: {
    storyUpdatePosition: {
      payload: boolean;
    };
  };
}

interface StoryCreateResponse {
  data: {
    storyCreate: {
      payload: Story;
    };
  };
}

interface StoryUpdateFollowedStatusArgs {
  storyId: number;
  status: boolean;
}

interface StoryUpdateFollowedStatusResponse {
  data: {
    storyUpdateFollowedStatus: {
      payload: boolean;
    };
  };
}

interface StoryUpdateSubscriptionStatusArgs {
  storyId: number;
  status: boolean;
}

interface StoryUpdateSubscriptionStatusResponse {
  data: {
    storyUpdateSubscriptionStatus: {
      payload: boolean;
    };
  };
}

export interface StoryCreateFieldsInput {
  imageId: number | null;
  imageRightholder?: string;
  imageStatusCopyrightId?: StatusCopyright;
  title: string;
  subtitle?: string;
  status?: StoryStatus;
  source?: StoryCreateFieldsSourceInput;
  localeId?: number;
  pinned?: StoryPinned;
  pinned2?: StoryPinned;
  pinned3?: StoryPinned;
  pinned4?: StoryPinned;
  pinned5?: StoryPinned;
  settings?: {
    access?: StoryAccess[];
    includeInWebappNewsFeed?: boolean;
    includeInNewsFeed?: boolean;
    includeInMixTab?: boolean;
    includeInMixTab2?: boolean;
    includeInMixTab3?: boolean;
    includeInMixTab4?: boolean;
    includeInMixTab5?: boolean;
    allowComments?: boolean;
    allowCardMentions?: boolean;
    allowSharing?: boolean;
    autoNotification?: StoryAutoNotification;
    allowCardReactions?: boolean;
    horizontalDisplayView: StoryHorizontalDisplayView | null;
    highlightingComments?: StoryHighlightingCommentsMode;
    cardOrder?: StoryCardOrder;
    displayItemUpdatedTime?: StoryDisplayItemUpdatedTime;
    feedFollowBehavior?: FeedFollowBehavior;
    shouldSyncItemPositionsOnConnectedStories?: boolean;
    shouldRestrictDuplicateUrlOfCardsInTimeFrame?: RestrictDuplicateUrlOfCardsInTimeFrame;
    shouldUpdatePostedTimeByComments?: boolean;
    cardLimit?: StoryCardLimit;
    showAllAuthors?: boolean;
    cardStyles?: StoryCardStyles;
  };
  organisationTags?: {
    externalTagsId?: number[];
    externalStatusTagsId?: number[];
    internalTagsId?: number[];
  };
  writeAccess?: {
    userIds?: number[];
    userTagIds?: number[];
  };
}

interface StoryCreateArgs {
  channelId: number;
  fields: StoryCreateFieldsInput;
}

interface SourceStoryByStoryIdArgs {
  storyId: number;
}

interface SourceStoryByStoryIdPayload {
  story: SourceStory;
  channel: SourceChannel;
}

interface SourceStoryByStoryIdResponse {
  data: {
    sourceStoryByStoryId: {
      payload: SourceStoryByStoryIdPayload;
    };
  };
}

export const storyApi = graphqlApiWithPersisted
  .enhanceEndpoints({ addTagTypes: ['FollowableStories'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      storyById: builder.query<Story, StoriesByIdArgs>({
        query: ({ storyId }) => ({
          url: '/graphql/webapp?storyById',
          method: 'POST',
          body: {
            query: `query StoryById($storyId: Int!) {
              storyById(storyId: $storyId) {
                payload {
                  ${storyQuery({ isAdminLayout: isAdminLayout() })}
                }
              }
            }`,
            variables: { storyId },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: StoriesByIdResponse) => response.data.storyById.payload,
      }),
      storiesByChannelId: builder.query<Story[], StoriesByChannelIdArgs>({
        query: ({ channelId, filter }) => ({
          url: '/graphql/webapp?storiesByChannelId',
          method: 'POST',
          body: {
            query: `query StoriesByChannelId($channelId: Int!, $filter: GetStoriesByChannelIdFilterArgs) {
              storiesByChannelId(channelId: $channelId, filter: $filter) {
                items {
                  ${storyQuery({ isAdminLayout: isAdminLayout() })}
                }
              }
            }`,
            variables: { channelId, filter },
          },
        }),
        forceRefetch: () => true,
        providesTags: (_, __, { filter }) =>
          filter?.feedFollowedStatus === undefined ? [] : ['FollowableStories'],
        transformResponse: (response: StoriesByChannelIdResponse) =>
          response.data.storiesByChannelId.items,
      }),
      storyDelete: builder.mutation<boolean, StoryDeleteArgs>({
        query: ({ storyId }) => ({
          url: '/graphql/webapp?storyDelete',
          method: 'POST',
          body: {
            query: `mutation StoryDelete($input: StoryDeleteInput!) {
              storyDelete(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { storyId } },
          },
        }),
        transformResponse: (response: StoryDeleteResponse) => response.data.storyDelete.payload,
      }),
      storyUpdate: builder.mutation<Story, StoryUpdateArgs>({
        query: ({ storyId, fields }) => ({
          url: '/graphql/webapp?storyUpdate',
          method: 'POST',
          body: {
            query: `mutation StoryUpdate($input: StoryUpdateInput!) {
              storyUpdate(input: $input) {
                payload {
                  ${storyQuery({ isAdminLayout: isAdminLayout() })}
                }
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { storyId, fields } },
          },
        }),
        transformResponse: (response: StoryUpdateResponse) => response.data.storyUpdate.payload,
      }),
      storyUpdatePosition: builder.mutation<boolean, StoryUpdatePositionArgs>({
        query: ({ storyId, position }) => ({
          url: '/graphql/webapp?storyUpdatePosition',
          method: 'POST',
          body: {
            query: `mutation StoryUpdatePosition($input: StoryUpdatePositionInput!) {
              storyUpdatePosition(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { storyId, position },
            },
          },
        }),
        transformResponse: (response: StoryUpdatePositionResponse) =>
          response.data.storyUpdatePosition.payload,
      }),
      storyCreate: builder.mutation<Story, StoryCreateArgs>({
        query: ({ channelId, fields }) => ({
          url: '/graphql/webapp?storyCreate',
          method: 'POST',
          body: {
            query: `mutation StoryCreate($input: StoryCreateInput!) {
              storyCreate(input: $input) {
                payload {
                  ${storyQuery({ isAdminLayout: isAdminLayout() })}
                }
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { channelId, fields },
            },
          },
        }),
        transformResponse: (response: StoryCreateResponse) => response.data.storyCreate.payload,
      }),
      sourceStoryByStoryId: builder.query<SourceStoryByStoryIdPayload, SourceStoryByStoryIdArgs>({
        query: ({ storyId }) => ({
          url: '/graphql/webapp?sourceStoryByStoryId',
          method: 'POST',
          body: {
            query: `query SourceStoryByStoryId($storyId: Int!) {
              sourceStoryByStoryId(storyId: $storyId) {
                payload {
                  story {
                    id
                  }
                  channel {
                    subdomain
                  }
                }
              }
            }`,
            variables: { storyId },
          },
        }),
        transformResponse: (response: SourceStoryByStoryIdResponse) =>
          response.data.sourceStoryByStoryId.payload,
      }),
      storyUpdateFollowedStatus: builder.mutation<boolean, StoryUpdateFollowedStatusArgs>({
        query: ({ storyId, status }) => ({
          url: '/graphql/webapp?storyUpdateFollowedStatus',
          method: 'POST',
          body: {
            query: `mutation StoryUpdateFollowedStatus($input: StoryUpdateFollowedStatusInput!) {
              storyUpdateFollowedStatus(input: $input) {
                payload
                error {
                  ... on StoryAccessError {
                    message
                  }
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { storyId, status },
            },
          },
        }),
        invalidatesTags: ['FollowableStories'],
        transformResponse: (response: StoryUpdateFollowedStatusResponse) =>
          response.data.storyUpdateFollowedStatus.payload,
      }),
      storyUpdateSubscriptionStatus: builder.mutation<boolean, StoryUpdateSubscriptionStatusArgs>({
        query: ({ storyId, status }) => ({
          url: '/graphql/webapp?storyUpdateSubscriptionStatus',
          method: 'POST',
          body: {
            query: `mutation StoryUpdateSubscriptionStatus($input: StoryUpdateSubscriptionStatusInput!) {
              storyUpdateSubscriptionStatus(input: $input) {
                payload
                error {
                  ... on StoryAccessError {
                    message
                  }
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { storyId, status },
            },
          },
        }),
        transformResponse: (response: StoryUpdateSubscriptionStatusResponse) =>
          response.data.storyUpdateSubscriptionStatus.payload,
      }),
    }),
  });

export const {
  useStoryByIdQuery,
  useStoriesByChannelIdQuery,
  useSourceStoryByStoryIdQuery,
  useStoryUpdateFollowedStatusMutation,
  useStoryUpdateSubscriptionStatusMutation,
} = storyApi;
